import React, { FC, useState } from 'react';
import { Text, Flex } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { fetchReportActivityHistory } from 'api/reports';
import { CustomTable } from 'components/modules/CustomTable';
import { Report } from 'types/report';
import { ReportActivity } from 'types/reportActivity';
import { convertUTCToLocal } from 'utils/dateUtils';

type ActivityHistoryTabProps = {
  report: Report;
};

const ActivityHistoryTab: FC<ActivityHistoryTabProps> = ({ report }) => {
  const [page, setPage] = useState<number>(1);
  const limit = 10; // Items per page

  const queryKey = [`fetch-report-activity-history-${report._id}`, page];

  const queryFn = () => fetchReportActivityHistory(report._id, page, limit);

  const options = {
    enabled: !!report._id,
    keepPreviousData: true,
    retry: false, // Don't retry on error
    refetchOnWindowFocus: false, // Don't refetch on window focus
    throwOnError: false, // Don't throw errors to error boundary
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const activityHistory = data?.item || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const getEmployeeName = (employee: ReportActivity['employee']) => {
    if (!employee) {
      return 'Unknown';
    }
    return `${employee.fname} ${employee.lname}`;
  };

  return (
    <Flex flexDir="column" p="20px">
      <CustomTable
        minW="1000px"
        data={activityHistory}
        isLoading={isLoading || isFetching}
        pagination={pagination}>
        {{
          headerRow: (
            <Flex w="100%">
              <Text w="20%" textAlign="left">
                Employee
              </Text>
              <Text w="25%" textAlign="left">
                Action
              </Text>
              <Text w="45%" textAlign="left">
                Description
              </Text>
              <Text w="10%" textAlign="left">
                Created At
              </Text>
            </Flex>
          ),
          renderRow: (activity: ReportActivity) => (
            <Flex w="100%">
              <Text w="20%" textAlign="left">
                {getEmployeeName(activity.employee)}
              </Text>
              <Text w="25%" textAlign="left">
                {activity.action}
              </Text>
              <Text w="45%" textAlign="left">
                {activity.description}
              </Text>
              <Text w="10%" textAlign="left">
                {convertUTCToLocal(activity.createdAt, true)}
              </Text>
            </Flex>
          ),
        }}
      </CustomTable>
    </Flex>
  );
};

export default ActivityHistoryTab;
